import axios, { type AxiosResponse } from 'axios'
import { ref } from 'vue'
import { updateCreditFromHeaders } from './creditHeaders'
import { useAppStore, useAuthStore } from '@/store'
import { globalEventEmitter } from '@/utils/eventEmitter'
import { getToken, getTokenDify, getTokenPassPort } from '@/store/modules/auth/helper'
// Function to show insufficient credit modal
export const showInsufficientCreditModal = () => {
  globalEventEmitter.emit('show-insufficient-credit-modal')
}

// Function to show upgrade to pro modal
export const showUpgradeToProModal = () => {
  globalEventEmitter.emit('show-upgrade-to-pro-modal')
}

// Function to show session expired modal
export const showSessionExpiredModal = () => {
  globalEventEmitter.emit('show-session-expired-modal')
}

const ContentType = {
  urlencoded: 'application/x-www-form-urlencoded;charset=UTF-8',
  json: 'application/json',
  formData: 'multipart/form-data',
}

const hiddenError = ref(false)

const showErrorMessage = (message: string) => {
  if (window.$message)
    window.$message.error(message)
  else console.error(message)
}

// Common request interceptor config
const requestInterceptor = (config: any) => {
  hiddenError.value = config.url.includes('users/detail_user')

  // Get auth store instance
  const authStore = useAuthStore()

  // Sync check: If localStorage is cleared but store still has token, clear store
  if (authStore.token && !getToken())
    authStore.removeToken()

  if (authStore.token_dify && !getTokenDify())
    authStore.removeTokenDify()

  if (authStore.token_pass_port && !getTokenPassPort())
    authStore.setTokenPassPort(undefined)

  const token = authStore.token
  const tokenDify = authStore.token_dify
  const tokenPassPort = authStore.token_pass_port
  const language = useAppStore().language

  if (token) {
    config.headers = config.headers ?? {}
    config.headers.Authorization = `Bearer ${token}`
  }
  if (tokenDify)
    config.headers['X-token-dify'] = tokenDify

  if (tokenPassPort)
    config.headers['X-pass-port'] = tokenPassPort

  config.headers['X-Language'] = language || 'vi-VN'

  // Set content-type depending on data
  if (config.data instanceof FormData)
    config.headers['Content-Type'] = ContentType.formData
  else
    config.headers['Content-Type'] = ContentType.json

  return config
}

const requestErrorHandler = (error: any) => Promise.reject(error)

// Common response interceptor success handler
const responseInterceptor = (res: AxiosResponse): Promise<AxiosResponse> => {
  if (res.status === 200 || res.status === 201) {
    // Update credit from response headers if present
    updateCreditFromHeaders(res)
    return Promise.resolve(res)
  }
  return Promise.reject(new Error('Response error'))
}

// Common response interceptor error handler
const responseErrorHandler = (error: any) => {
  // Handle unauthorized error (401)
  if (error.response.status === 401) {
    showSessionExpiredModal()
    return
  }
  if (!hiddenError.value) {
    if (error.response) {
      // Handle insufficient credit error (402)
      if (error.response.status === 402) {
        showInsufficientCreditModal()
        return Promise.reject(error)
      }
      // Handle upgrade to pro error (512)
      if (error.response.status === 512) {
        showUpgradeToProModal()
        return Promise.reject(error)
      }
      if (error.response.status === 600
				|| error.response.status === 601
				|| error.response.status === 404
				|| error.response.status === 406
				|| error.response.status === 514)
        return Promise.reject(error)

      const msg = error.response.data?.message || error.response.data?.error || 'An error occurred'
      showErrorMessage(msg)
    }
    else if (error.request) {
      showErrorMessage('No response from server')
    }
    else {
      showErrorMessage(error.message || 'Request failed')
    }
  }

  return Promise.reject(error)
}

function createAxiosInstance(baseURL: string, timeout = 60000, extraHeaders = {}) {
  const instance = axios.create({
    baseURL,
    timeout,
    responseType: 'json',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      ...extraHeaders,
    },
  })

  instance.interceptors.request.use(requestInterceptor, requestErrorHandler)
  instance.interceptors.response.use(responseInterceptor, responseErrorHandler)

  return instance
}

const baseAppService = createAxiosInstance(import.meta.env.VITE_GLOB_API_URL)
const baseAppServiceAuth = createAxiosInstance(import.meta.env.VITE_GLOB_API_AUTH_URL)

export { baseAppService, baseAppServiceAuth }
